/**
 * GrantFormFields — per-engine fields for GrantEditFormBody (Slice F2).
 *
 * Renders the GRANT/REVOKE form: mode toggle, privileges multi-select +
 * free-form add, on-object picker (kind / schema / name), grantees list
 * (kind / name / mysql host), WITH GRANT OPTION + per-engine extras.
 */
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { GrantObjectKind, GrantPrincipalKind } from '../../../../sql-resolver/types/ddlEdit';
import {
  getCommonPrivilegesForKind,
  getSupportedObjectKindsForEngine,
  type GrantEditMode,
  type GrantFormGrantee,
  type GrantFormValues,
} from './grantFormHelpers';

export interface GrantFormFieldsProps {
  readonly engine: SqlEngine;
  readonly values: GrantFormValues;
  readonly setValues: Dispatch<SetStateAction<GrantFormValues>>;
  readonly testId: string;
}

const KIND_LABELS: Record<GrantObjectKind, string> = {
  table: 'Table', view: 'View', sequence: 'Sequence', schema: 'Schema',
  database: 'Database', function: 'Function', procedure: 'Procedure',
  routine: 'Routine', package: 'Package', type: 'Type', tablespace: 'Tablespace',
  role: 'Role (membership)', system: 'System privilege',
};

const PRINCIPAL_LABELS: Record<GrantPrincipalKind, string> = {
  user: 'User', role: 'Role', public: 'PUBLIC',
};

export function GrantFormFields(props: GrantFormFieldsProps) {
  const { engine, values, setValues, testId } = props;
  const update = (patch: Partial<GrantFormValues>) =>
    setValues((prev) => ({ ...prev, ...patch }));
  const supportedKinds = getSupportedObjectKindsForEngine(engine);
  const commonPrivs = getCommonPrivilegesForKind(values.objectKind);
  const isOracleSystem = engine === 'oracle' && values.objectKind === 'system';
  const isRoleGrant = values.objectKind === 'role';

  return (
    <div className="flex flex-col gap-3">
      <ModeToggle mode={values.mode} onChange={(m) => update({ mode: m })} testId={testId} />

      <Field label="Object kind" testId={`${testId}-object-kind`}>
        <Select
          value={values.objectKind}
          onValueChange={(v) => update({ objectKind: v as GrantObjectKind })}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-object-kind-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {supportedKinds.map((k) => (
              <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {!isOracleSystem ? (
        <div className="flex gap-2">
          {/* Role-membership grants carry no schema-qualified object — the
              role name IS the privilege (GRANT <role> TO <principal>). Hiding
              the schema field for role grants avoids implying it has any
              effect (buildGrantObject ignores it). */}
          {!isRoleGrant ? (
            <Field label="Schema (optional)" testId={`${testId}-object-schema`}>
              <Input
                value={values.objectSchema}
                onChange={(e: ChangeEvent<HTMLInputElement>) => update({ objectSchema: e.target.value })}
                data-testid={`${testId}-object-schema-input`}
              />
            </Field>
          ) : null}
          <Field label={isRoleGrant ? 'Role name' : 'Object name'} testId={`${testId}-object-name`}>
            <Input
              value={values.objectName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => update({ objectName: e.target.value })}
              data-testid={`${testId}-object-name-input`}
            />
          </Field>
        </div>
      ) : null}

      {/* Role-membership grants have no privilege list — the role name (typed
          above) IS the privilege. Rendering the SELECT/INSERT chips here is
          what let the stale default `['SELECT']` leak into the emitted grant
          (bug F23), so the field is suppressed for role grants. */}
      {!isRoleGrant ? (
        <PrivilegesField
          values={values}
          commonPrivs={commonPrivs}
          update={update}
          testId={testId}
          showPgsqlAll={engine === 'postgres'}
        />
      ) : null}

      <GranteesField engine={engine} values={values} update={update} testId={testId} />

      {values.mode === 'grant' ? (
        <>
          <Toggle
            label={engine === 'oracle' && values.oracleWithAdminOption ? 'WITH GRANT OPTION (disabled — ADMIN OPTION set)' : 'WITH GRANT OPTION'}
            checked={values.withGrantOption}
            onChange={(c) => update({ withGrantOption: c })}
            testId={`${testId}-with-grant-option`}
            disabled={engine === 'oracle' && values.oracleWithAdminOption}
          />
          {engine === 'oracle' ? (
            <Toggle
              label="WITH ADMIN OPTION (system / role grants)"
              checked={values.oracleWithAdminOption}
              onChange={(c) => update({ oracleWithAdminOption: c, ...(c ? { withGrantOption: false } : {}) })}
              testId={`${testId}-oracle-admin-option`}
            />
          ) : null}
        </>
      ) : (
        <>
          {engine === 'mssql' || engine === 'postgres' ? (
            <Toggle
              label="CASCADE"
              checked={values.cascade}
              onChange={(c) => update({ cascade: c })}
              testId={`${testId}-cascade`}
            />
          ) : null}
          {engine === 'postgres' ? (
            <Toggle
              label="GRANT OPTION FOR (revoke only the grant option)"
              checked={values.pgsqlGrantOptionFor}
              onChange={(c) => update({ pgsqlGrantOptionFor: c })}
              testId={`${testId}-pgsql-grant-option-for`}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function ModeToggle({
  mode, onChange, testId,
}: {
  mode: GrantEditMode;
  onChange: (m: GrantEditMode) => void;
  testId: string;
}) {
  return (
    <Field label="Mode" testId={`${testId}-mode`}>
      <div className="flex gap-1" data-testid={`${testId}-mode-group`}>
        {(['grant', 'revoke'] as const).map((m) => (
          <Button
            key={m}
            type="button"
            variant={mode === m ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(m)}
            data-testid={`${testId}-mode-${m}`}
          >
            {m.toUpperCase()}
          </Button>
        ))}
      </div>
    </Field>
  );
}

function PrivilegesField({
  values, commonPrivs, update, testId, showPgsqlAll,
}: {
  values: GrantFormValues;
  commonPrivs: readonly string[];
  update: (patch: Partial<GrantFormValues>) => void;
  testId: string;
  showPgsqlAll: boolean;
}) {
  const togglePriv = (priv: string) => {
    const set = new Set(values.privileges);
    if (set.has(priv)) set.delete(priv);
    else set.add(priv);
    update({ privileges: Array.from(set) });
  };
  const setFreeFormText = (txt: string) => {
    const items = txt.split(',').map((p) => p.trim().toUpperCase()).filter((p) => p.length > 0);
    update({ privileges: items });
  };
  return (
    <Field label="Privileges" testId={`${testId}-privileges`}>
      {showPgsqlAll ? (
        <Toggle
          label="ALL PRIVILEGES (postgres shortcut)"
          checked={values.pgsqlAllPrivileges}
          onChange={(c) => update({ pgsqlAllPrivileges: c })}
          testId={`${testId}-pgsql-all-privileges`}
        />
      ) : null}
      {commonPrivs.length > 0 && !values.pgsqlAllPrivileges ? (
        <div className="flex flex-wrap gap-1" data-testid={`${testId}-privilege-chips`}>
          {commonPrivs.map((p) => (
            <Button
              key={p}
              type="button"
              variant={values.privileges.includes(p) ? 'default' : 'outline'}
              size="sm"
              onClick={() => togglePriv(p)}
              data-testid={`${testId}-privilege-${p.replace(/\s+/g, '-')}`}
            >
              {p}
            </Button>
          ))}
        </div>
      ) : null}
      {!values.pgsqlAllPrivileges ? (
        <Input
          value={values.privileges.join(', ')}
          placeholder="SELECT, INSERT"
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFreeFormText(e.target.value)}
          data-testid={`${testId}-privileges-input`}
        />
      ) : null}
    </Field>
  );
}

function GranteesField({
  engine, values, update, testId,
}: {
  engine: SqlEngine;
  values: GrantFormValues;
  update: (patch: Partial<GrantFormValues>) => void;
  testId: string;
}) {
  const updateGrantee = (idx: number, patch: Partial<GrantFormGrantee>) => {
    const next = values.grantees.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    update({ grantees: next });
  };
  const addGrantee = () =>
    update({ grantees: [...values.grantees, { kind: 'user', name: '', mysqlHost: '' }] });
  const removeGrantee = (idx: number) => {
    if (values.grantees.length <= 1) return;
    update({ grantees: values.grantees.filter((_, i) => i !== idx) });
  };
  return (
    <Field label="Grantees" testId={`${testId}-grantees`}>
      <div className="flex flex-col gap-1">
        {values.grantees.map((g, idx) => (
          <div key={idx} className="flex gap-1 items-center" data-testid={`${testId}-grantee-${idx}`}>
            <Select
              value={g.kind}
              onValueChange={(v) => updateGrantee(idx, { kind: v as GrantPrincipalKind })}
            >
              <SelectTrigger
                className="h-8 w-auto shrink-0 gap-1 text-xs"
                data-testid={`${testId}-grantee-${idx}-kind`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['user', 'role', 'public'] as const).map((k) => (
                  <SelectItem key={k} value={k}>{PRINCIPAL_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {g.kind !== 'public' ? (
              <>
                <Input
                  value={g.name}
                  placeholder="name"
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    updateGrantee(idx, { name: e.target.value })}
                  data-testid={`${testId}-grantee-${idx}-name`}
                />
                {engine === 'mysql' && g.kind === 'user' ? (
                  <Input
                    value={g.mysqlHost}
                    placeholder="host (default %)"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      updateGrantee(idx, { mysqlHost: e.target.value })}
                    data-testid={`${testId}-grantee-${idx}-host`}
                  />
                ) : null}
              </>
            ) : null}
            {values.grantees.length > 1 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeGrantee(idx)}
                data-testid={`${testId}-grantee-${idx}-remove`}
              >
                Remove
              </Button>
            ) : null}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addGrantee}
          data-testid={`${testId}-grantees-add`}
        >
          Add grantee
        </Button>
      </div>
    </Field>
  );
}

function Field({
  label, children, testId,
}: { label: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="flex flex-col gap-1" data-testid={`${testId}-field`}>
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({
  label, checked, onChange, testId, disabled,
}: { label: string; checked: boolean; onChange: (c: boolean) => void; testId: string; disabled?: boolean }) {
  return (
    <label className="flex items-center gap-2 text-xs" data-testid={testId}>
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(c) => onChange(c === true)}
        data-testid={`${testId}-input`}
      />
      <span>{label}</span>
    </label>
  );
}
